import { useEffect, useState } from "react";
import { Menu, X, ArrowRight, Flame } from "lucide-react";
import { scrollToId } from "../lib/scroll";
import { trackClickCTA } from "../lib/pixel";
import { Countdown } from "./Countdown";
import { PromoCode } from "./PromoCode";
import { usePresaleCountdown } from "../lib/presale";

const LINKS = [
    { label: "O kursie", hash: "#o-kursie", testid: "nav-link-o-kursie" },
    { label: "Co otrzymujesz", hash: "#co-otrzymujesz", testid: "nav-link-co-otrzymujesz" },
    { label: "Program", hash: "#program", testid: "nav-link-program" },
    { label: "Prowadzące", hash: "#prowadzace", testid: "nav-link-prowadzace" },
    { label: "Opinie", hash: "#opinie", testid: "nav-link-opinie" },
    { label: "Pakiety", hash: "#pakiety", testid: "nav-link-pakiety" },
    { label: "FAQ", hash: "#faq", testid: "nav-link-faq" },
];

export const Header = () => {
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);
    const { ended, lastDay, promoActive, promoTime } = usePresaleCountdown();

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 24);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    const go = (hash) => {
        setOpen(false);
        scrollToId(hash);
    };

    return (
        <header data-testid="sticky-header" className="fixed inset-x-0 top-0 z-50">
            <div
                data-testid="presale-top-bar"
                className="bg-presale text-white text-center text-[11px] sm:text-sm font-bold py-2 px-4 flex flex-wrap items-center justify-center gap-x-2.5 gap-y-0.5 uppercase tracking-[0.12em]"
            >
                <Flame className="w-3.5 h-3.5 text-tang shrink-0" aria-hidden="true" />
                {ended ? (
                    <span>Early Bird zakończony · premiera kursu 13.11.2026</span>
                ) : promoActive ? (
                    <>
                        {promoTime.lastDay ? (
                            <>
                                <span className="hidden sm:inline">
                                    Tylko dziś! Kod <PromoCode /> = -50 zł, koniec o północy
                                </span>
                                <span className="sm:hidden">Tylko dziś! -50 zł, kod MINUS50</span>
                            </>
                        ) : (
                            <>
                                <span className="hidden sm:inline">
                                    Tylko 27 października: -50 zł z kodem <PromoCode />
                                </span>
                                <span className="sm:hidden">27.10: -50 zł z kodem MINUS50</span>
                            </>
                        )}
                        <span className="hidden sm:inline text-white/50" aria-hidden="true">
                            ·
                        </span>
                        <Countdown target="promo" />
                    </>
                ) : (
                    <>
                        {lastDay ? (
                            <>
                                <span className="hidden sm:inline">
                                    Ostatni dzień Early Bird! Koniec dziś o północy
                                </span>
                                <span className="sm:hidden">Ostatni dzień! Koniec o północy</span>
                            </>
                        ) : (
                            <>
                                <span className="hidden sm:inline">
                                    Early Bird kończy się 30 października o północy
                                </span>
                                <span className="sm:hidden">
                                    Koniec Early Bird: 30.10, północ
                                </span>
                            </>
                        )}
                        <span className="hidden sm:inline text-white/50" aria-hidden="true">
                            ·
                        </span>
                        <Countdown />
                    </>
                )}
                <Flame className="w-3.5 h-3.5 text-tang shrink-0 hidden sm:block" aria-hidden="true" />
            </div>
            <div
                className={`transition-all duration-500 ${
                    scrolled
                        ? "bg-white/85 backdrop-blur-xl shadow-soft border-b border-border"
                        : "bg-transparent"
                }`}
            >
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-[72px] flex items-center justify-between gap-4">
                    <a
                        href="#start"
                        data-testid="header-logo"
                        onClick={(e) => {
                            e.preventDefault();
                            window.__lenis
                                ? window.__lenis.scrollTo(0, { duration: 1.2 })
                                : window.scrollTo({ top: 0, behavior: "smooth" });
                        }}
                        className="shrink-0"
                        aria-label="EduNation - strona główna kursu"
                    >
                        <img src="/images/logo.svg" alt="EduNation" className="h-7 w-auto" />
                    </a>

                    <nav className="hidden lg:flex items-center gap-7" aria-label="Nawigacja główna">
                        {LINKS.map((l) => (
                            <button
                                key={l.hash}
                                data-testid={l.testid}
                                onClick={() => go(l.hash)}
                                className="text-sm font-medium text-ink-soft hover:text-ink transition-colors duration-200 min-h-[44px]"
                            >
                                {l.label}
                            </button>
                        ))}
                    </nav>

                    <div className="flex items-center gap-3">
                        <button
                            data-testid="nav-cta-button"
                            onClick={() => {
                                trackClickCTA("header");
                                go("#pakiety");
                            }}
                            className="hidden sm:inline-flex items-center gap-2 rounded-full bg-tang hover:bg-[#EFE238] text-ink text-sm font-bold px-5 min-h-[44px] transition-all duration-300 hover:-translate-y-0.5"
                        >
                            Wybieram swój pakiet
                            <ArrowRight className="w-4 h-4" />
                        </button>
                        <button
                            data-testid="mobile-menu-button"
                            onClick={() => setOpen(!open)}
                            className="lg:hidden inline-flex items-center justify-center w-11 h-11 rounded-full border border-border bg-white/80 text-ink"
                            aria-label={open ? "Zamknij menu" : "Otwórz menu"}
                            aria-expanded={open}
                        >
                            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
                        </button>
                    </div>
                </div>

                {open && (
                    <nav
                        data-testid="mobile-menu"
                        className="lg:hidden bg-white/95 backdrop-blur-xl border-t border-border px-6 py-4 flex flex-col"
                        aria-label="Nawigacja mobilna"
                    >
                        {LINKS.map((l) => (
                            <button
                                key={l.hash}
                                data-testid={`${l.testid}-mobile`}
                                onClick={() => go(l.hash)}
                                className="text-left text-base font-medium text-ink py-3 min-h-[44px] border-b border-border/60 last:border-0"
                            >
                                {l.label}
                            </button>
                        ))}
                    </nav>
                )}
            </div>
        </header>
    );
};
